import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { IPills } from '@nibtravel/shared/ui/components/pills';
import { isEqual, uniqBy } from 'lodash-es';
import { BehaviorSubject, isObservable, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { BaseInputComponent } from '../../../common/base-input.component';
import {
  DefaultAutocompleteSearchKeyFilter,
  IAutocompleteSearchKeyFilter,
} from './autocomplete-helper';
import {
  AutocompleteValueSelectEvent,
  IAutocompleteOption,
} from './autocomplete.model';
import { PopularCountrySuggestionsComponent } from '../../../../../../qqc/features/destination/src/lib/popularCountrySuggestions/popularCountrySuggestions.component';
import { AutocompleteHandlerComponent } from './autocomplete-handler.component';

@Component({
  selector: 'nibtravel-autocomplete',
  templateUrl: './autocomplete.component.html',
})
export class AutocompleteComponent
  extends BaseInputComponent
  implements OnDestroy
{
  private internalOptions: IAutocompleteOption[] = [];
  private internalSelectedOptions: IAutocompleteOption[] = [];
  private internalSearchKeyFilters: IAutocompleteSearchKeyFilter[] = [
    new DefaultAutocompleteSearchKeyFilter(),
  ];
  private destroy$ = new Subject<void>();
  private isShowDestinationSuggestion$ = new BehaviorSubject<boolean>(false);

  searchKey = '';
  set selectedOptions(value: IAutocompleteOption[]) {
    if (!isEqual(this.internalSelectedOptions, value)) {
      this.internalSelectedOptions = value;
      this.notifySelectionChange();
      this.populateSelectedPills();
    }
  }
  get selectedOptions(): IAutocompleteOption[] {
    return this.internalSelectedOptions;
  }
  filteredOptions: IAutocompleteOption[] = [];
  selectedPills: IPills[] = [];

  @ViewChild('autocompleteHandler')
  autocompleteHandler!: AutocompleteHandlerComponent;
  @Input() popularCountrySuggestions!: PopularCountrySuggestionsComponent;
  @HostBinding('class') class = 'autocomplete';
  @HostBinding('class.input') isInput = true;
  @Input() isMultipleSelection = true;
  @Input() isUniqueSelection = true;
  @Input() searchHints =
    'Please list all countries and regions you are travelling to.';
  @Input() noResultHints = 'No result...';
  @Input() set options(
    value: IAutocompleteOption[] | Observable<IAutocompleteOption[]>
  ) {
    if (isObservable(value)) {
      value.pipe(takeUntil(this.destroy$)).subscribe((options) => {
        this.internalOptions = options;
        this.populateFilterOptions();
        this.cd.markForCheck();
      });
    } else {
      this.internalOptions = value;
      this.populateFilterOptions();
      this.cd.markForCheck();
    }
  }
  @Input() set searchKeyFilters(
    value: IAutocompleteSearchKeyFilter[] | IAutocompleteSearchKeyFilter
  ) {
    if (!value) {
      this.internalSearchKeyFilters = [
        new DefaultAutocompleteSearchKeyFilter(),
      ];
      return;
    }
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return;
      }
      this.internalSearchKeyFilters = [...value];
    } else {
      this.internalSearchKeyFilters = [value];
    }
  }
  @Output() searchKeyChanged = new EventEmitter<string>();
  @Output() filteredOptionsPopulated = new EventEmitter<
    IAutocompleteOption[]
  >();
  @Output() selectedOptionsChanged = new EventEmitter<IAutocompleteOption[]>();

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onInputChange(searchKey: string): void {
    this.searchKey = searchKey;
    this.changeDestinationSuggestion(false);
    if (searchKey === '') {
      this.changeDestinationSuggestion(true);
    }
    this.populateFilterOptions();
    this.notifySearchKeyChanged();
    if (!this.isMultipleSelection) {
      this.clearSelectedOptions();
    }
  }

  onOptionsPanelClose(): void {
    if (this.isMultipleSelection) {
      this.resetSearchKey();
    }
    this.changeDestinationSuggestion(false);
  }

  onOptionSelected(event: AutocompleteValueSelectEvent): void {
    this.setOptionAsSelectedOption(event.option);
    this.autocompleteHandler.isHidden = true;
  }

  onOptionRemoved(pill: IPills): void {
    this.removeSelectedOptionByPill(pill);
    this._onTouched();
  }

  clearSelectedOptions(): void {
    this.selectedOptions = [];
    this.clearSelectedPills();
    this.cd.markForCheck();
  }

  noResultsFound(): string | boolean {
    return !this.filteredOptions.length && this.searchKey;
  }

  noSearchKey(): string | boolean {
    return (
      !this.searchKey &&
      (!this.filteredOptions || this.filteredOptions.length === 0)
    );
  }

  public isShowDestinationSuggestion(): Observable<boolean> {
    return this.isShowDestinationSuggestion$.asObservable();
  }

  public changeDestinationSuggestion(value: boolean): void {
    this.isShowDestinationSuggestion$.next(value);
  }

  writeValue(value: any): void {
    if (value && Array.isArray(value)) {
      this.selectedOptions = value.reduce((acc, curr) => {
        return [...acc, this.internalOptions.find((o) => o.value === curr)];
      }, []);

      if (!this.isMultipleSelection && this.selectedOptions.length) {
        this.searchKey = this.selectedOptions[0].text;
      }
    }
    super.writeValue(value);
  }

  setOptionAsSelectedOption(option: IAutocompleteOption): void {
    let options = [option];
    if (this.isMultipleSelection) {
      options = [...this.selectedOptions, option];
    }
    if (this.isUniqueSelection) {
      options = uniqBy(options, 'value');
    }
    this.selectedOptions = options;
    if (this.isMultipleSelection) {
      this.populateSelectedPills();
    } else {
      this.populateSearchKey();
    }
    this.cd.markForCheck();
  }

  private resetSearchKey(): void {
    this.searchKey = '';
    this.notifySearchKeyChanged();
    this.populateFilterOptions();
  }

  private notifySearchKeyChanged(): void {
    this.searchKeyChanged.emit(this.searchKey);
  }

  private notifySelectionChange(): void {
    const values = this.selectedOptions.map((o) => o.value);
    this.onChange(values?.length > 0 ? values : undefined);
    this.selectedOptionsChanged.emit(values);
  }

  private populateFilterOptions(): void {
    if (!this.searchKey) {
      // Don't display any result if no search key presented
      this.filteredOptions = [];
      this.filteredOptionsPopulated.emit(this.filteredOptions);
      return;
    }
    let filteredOptions = [...this.internalOptions];
    for (const searchKeyFilter of this.internalSearchKeyFilters) {
      filteredOptions = searchKeyFilter.filter(filteredOptions, this.searchKey);
    }
    this.filteredOptions = filteredOptions;
    this.filteredOptionsPopulated.emit(this.filteredOptions);
  }

  private populateSelectedPills(): void {
    this.selectedPills = this.selectedOptions.map((item) => {
      return {
        text: item.text,
      } as IPills;
    });
  }

  private clearSelectedPills(): void {
    this.selectedPills = [];
  }

  private populateSearchKey(): void {
    this.searchKey = this.selectedOptions.find((o) => o)?.text ?? '';
  }

  private removeSelectedOptionByPill(pill: IPills): void {
    const removeIndex = this.selectedOptions.findIndex(
      (result) => result.text === pill.text
    );
    if (removeIndex > -1) {
      this.selectedOptions = this.selectedOptions.filter(
        (_, i) => i !== removeIndex
      );
    }
    this.populateSelectedPills();
    this.cd.markForCheck();
  }

  onFocusHandler(event: any) {
    super.onFocus(event);
    this.changeDestinationSuggestion(true);
  }
}
